import React from 'react';
import type { PopulatedLead } from '../types';
import type { LeadCardAction } from './LeadCard';
import { Avatar, AvatarImage, AvatarFallback, CardActionsMenu, StatusPill } from '@/shared/components/ui';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { Icon } from '@/shared/icons';
import { buildLeadCardActions } from './leadCardActions';
import {
  formatLeadCurrency,
  formatLeadRelative,
  getLeadInitials,
  getLeadChannelLabel,
  getLeadPriorityMeta,
  getBusinessBadge,
  getBusinessPillVariant,
  getQualifyBadge,
  getQualifyPillVariant,
  hasLeadAlert,
  getLeadEmail,
} from './leadRowHelpers';
import { CHANNEL_ICONS, CHANNEL_COLORS } from './lead-visuals';

/**
 * Linha de tabela da listagem de leads (view "Lista").
 * Padrão-lista: checkbox (1ª col) + colunas multi-linha + menu ••• (última col).
 * Kebab = CardActionsMenu shared (F36/F77) com ações de `buildLeadCardActions`.
 */
export interface LeadListRowProps {
  lead: PopulatedLead;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onAction: (action: LeadCardAction, lead: PopulatedLead) => void;
}

/** `.pbadge` do mockup: 22px, radius 6, 12px semibold, tint `${color}1a` + dot 6px. */
export const LeadPriorityPill: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span
    className="inline-flex items-center gap-[5px] whitespace-nowrap font-semibold"
    style={{ height: 22, padding: '0 8px', borderRadius: 6, fontSize: 12, color, backgroundColor: `${color}1a` }}
  >
    <i className="rounded-full" style={{ width: 6, height: 6, backgroundColor: 'currentColor' }} />
    {label}
  </span>
);

export const LeadListRow: React.FC<LeadListRowProps> = ({ lead, selected, onToggleSelect, onAction }) => {
  const priority = getLeadPriorityMeta(lead.priority);
  const business = getBusinessBadge(lead.businessStatus);
  const qualify = getQualifyBadge(lead.qualifyStatus);
  const alert = hasLeadAlert(lead);
  const channelIcon = lead.channel ? (CHANNEL_ICONS[lead.channel] ?? 'tag') : undefined;
  const activityRelative = formatLeadRelative(lead.lastInteractionAt || lead.createdAt);

  return (
    <tr
      data-testid={`lead-row-${lead.id}`}
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect(lead.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`lead-check-${lead.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Data — mockup: 1 linha "18/07", 12px muted */}
      <td className="px-3.5 py-[11px] whitespace-nowrap text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        {(() => {
          if (!lead.createdAt) return '—';
          const d = new Date(lead.createdAt);
          return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        })()}
      </td>

      {/* Nome (avatar slate + nome + email) */}
      <td className="px-3.5 py-[11px]">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-[34px] h-[34px] flex-shrink-0" isSquircle>
            {lead.contact?.picture && <AvatarImage src={lead.contact.picture} alt={lead.contact?.name || ''} isSquircle />}
            <AvatarFallback isSquircle>
              <span className="text-white font-semibold text-xs">{getLeadInitials(lead.contact?.name)}</span>
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {lead.contact?.name || 'Sem nome'}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              <PrivacyText>{getLeadEmail(lead)}</PrivacyText>
            </div>
          </div>
        </div>
      </td>

      {/* Canal — mockup: pill com ícone colorido + label */}
      <td className="px-3.5 py-[11px]">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold whitespace-nowrap"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
        >
          {channelIcon && (
            <Icon
              name={channelIcon}
              className="w-3.5 h-3.5 flex-none"
              style={lead.channel ? { color: CHANNEL_COLORS[lead.channel] ?? 'var(--theme-muted-text)' } : undefined}
            />
          )}
          {getLeadChannelLabel(lead.channel)}
        </span>
      </td>

      {/* Funil */}
      <td className="px-3.5 py-[11px] text-sm" style={{ color: 'var(--theme-card-text)' }}>{lead.funnel?.name || '—'}</td>

      {/* Etapa */}
      <td className="px-3.5 py-[11px] text-sm" style={{ color: 'var(--theme-card-text)' }}>{lead.step?.name || '—'}</td>

      {/* Valor */}
      <td className="px-3.5 py-[11px] text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }}>
        {formatLeadCurrency(lead.budget)}
      </td>

      {/* Responsável — mockup: nome em texto simples */}
      <td className="px-3.5 py-[11px] whitespace-nowrap text-sm" style={{ color: 'var(--theme-card-text)' }}>
        {lead.assignee?.name || '—'}
      </td>

      {/* Prioridade — `.pbadge` */}
      <td className="px-3.5 py-[11px]">
        <LeadPriorityPill color={priority.color} label={priority.label} />
      </td>

      {/* Atividade — mockup: `act-alert` (vermelho semibold + sino) ou tempo relativo muted 12px */}
      <td className="px-3.5 py-[11px] whitespace-nowrap">
        {alert ? (
          <span className="inline-flex items-center gap-[5px] font-semibold" style={{ color: '#ef4444', fontSize: 12 }}>
            <Icon name="bell" className="w-3.5 h-3.5 flex-none" />
            {activityRelative || 'sem atividade'}
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)', fontSize: 12 }}>{activityRelative || '—'}</span>
        )}
      </td>

      {/* Status — StatusPill shared (F62) */}
      <td className="px-3.5 py-[11px]"><StatusPill variant={getBusinessPillVariant(lead.businessStatus)} label={business.label} /></td>

      {/* Qualificação — StatusPill shared (F62) */}
      <td className="px-3.5 py-[11px]"><StatusPill variant={getQualifyPillVariant(lead.qualifyStatus)} label={qualify.label} /></td>

      {/* Ações */}
      <td className="px-3.5 py-[11px] text-right w-14">
        <CardActionsMenu
          size="sm"
          actions={buildLeadCardActions(lead, (action, l) => onAction(action, l as PopulatedLead))}
          data-testid={`lead-actions-button-${lead.id}`}
        />
      </td>
    </tr>
  );
};

LeadListRow.displayName = 'LeadListRow';
